<template>
  <section
    class="product-size-block"
    :class="`product-size-block--${variant}`"
    aria-label="Frame measurements"
  >
    <div class="product-size-block__header">
      <span>Frame measurements</span>
      <router-link
        to="/size_guide"
        @click="trackBehaviorEvent('pdp_size_guide_open')"
        >Size guide</router-link
      >
    </div>
    <dl class="product-size-block__measurements">
      <div v-for="dimension in dimensionRows" :key="dimension.key">
        <dt>{{ dimension.label }}</dt>
        <dd v-if="dimension.value">{{ dimension.value }}<span> mm</span></dd>
        <dd v-else class="product-size-block__missing">Not listed</dd>
      </div>
    </dl>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { getProductPresentation } from "../../../../../utils/productPresentation";
import { trackBehaviorEvent } from "../../../../../utils/behaviorAnalytics";
const props = defineProps({
  product: { type: Object, default: () => null },
  // "standalone" sits on its own above Add to cart; "panel" is revealed by the
  // Size row of the identity list, which already carries the top rule.
  variant: {
    type: String,
    default: "standalone",
    validator: (value: string) => ["standalone", "panel"].includes(value),
  },
});
const dimensionRows = computed(() => {
  const dimensions = getProductPresentation(props.product).dimensions;
  return [
    { key: "lens", label: "Lens Width" },
    { key: "bridge", label: "Bridge Width" },
    { key: "temple", label: "Temple Width" },
  ].map((item) => ({
    ...item,
    value:
      dimensions
        .find((d) => d.key === item.key)
        ?.value.replace(/\s*mm\b/i, "") || "",
  }));
});
</script>

<style scoped lang="scss">
.product-size-block {
  border-bottom: 1px solid #e7e4df;
  padding: 16px 0;
  margin-bottom: 0;
  color: #101820;
}
.product-size-block--standalone {
  border-top: 1px solid #e7e4df;
}
// The identity list this panel drops out of already draws the rule above it.
.product-size-block--panel {
  padding-top: 14px;
}
.product-size-block__header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 16px;
  font-size: 13px;
  font-weight: 600;
  a {
    font-size: 12px;
    font-weight: 400;
    text-decoration: underline;
    text-underline-offset: 4px;
    padding: 6px 0;
  }
  a:focus-visible {
    outline: 2px solid #101820;
    outline-offset: 4px;
  }
}
.product-size-block__measurements {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 0;
  > div {
    min-width: 0;
  }
  dt {
    font-size: 12px;
    line-height: 1.5;
    color: #656772;
  }
  dd {
    margin: 4px 0 0;
    font-size: 24px;
    font-weight: 600;
    line-height: 1.25;
    overflow-wrap: anywhere;
    font-variant-numeric: tabular-nums;
  }
  dd span {
    font-size: 12px;
    font-weight: 400;
  }
  dd.product-size-block__missing {
    font-size: 13px;
    font-weight: 400;
    color: #656772;
    padding-top: 6px;
  }
}
</style>
